import {
  AgentActivityProtocol,
  AgentActivityRiskLevelParam,
} from "./hey-api-gen/tenant-mcp/index.ts";
import type * as TenantMcp from "./hey-api-gen/tenant-mcp/index.ts";

/** Client authentication methods a registered MCP client may use. */
export const AgenticClientAuthMethods = [
  "client_secret_post",
  "federated_jwt",
  /**
   * @deprecated Remove awareness of SPIFFE from controller at all
   */
  "jwt_svid",
] as const;
export type AgenticClientAuthMethod = (typeof AgenticClientAuthMethods)[number];

/** Client platforms accepted by the tenant client-registration API. */
export const AgenticClientPlatforms = [
  "claude-code",
  "claude-agents",
  "custom",
] as const;
export type AgenticClientPlatform = (typeof AgenticClientPlatforms)[number];

/** Lifecycle states for a registered MCP client. */
export const AgenticClientStatuses = ["active", "disabled"] as const;
export type AgenticClientStatus = (typeof AgenticClientStatuses)[number];

export type AgenticClientBase = {
  /** Epoch ms when this client was created */
  createdAt: number;
  /** Customer-managed human-friendly client name. */
  displayName?: string;
  /** Client identifier
   *
   * For confidential clients: the client ID
   * For federated clients: the client subject
   */
  id: string;
  /** Email of the P0 user who owns this client. Set to the registering user
   * at registration, but changeable afterwards, so it is independent of
   * `createdBy`. Absent for dynamically registered clients and for clients
   * registered before this field existed. */
  owner?: string;
  /** Whether this client may currently authenticate or refresh. Absent is
   * treated as "active" for clients registered before this field existed. */
  status?: AgenticClientStatus;
};

/** Represents a confidential MCP client in calls to o/:tenant/agentic/clients/... */
export type AgenticConfidentialClientBase = AgenticClientBase & {
  type: "client_credential_post";
  createdBy: { user: string; ip: string | undefined };
  hostname: string;
  platform: string;
  redirectUri: string;
  version: string;
};

/** Represents a federated MCP client */
export type AgenticFederatedClient = AgenticClientBase & {
  type: "jwt_bearer";
  /** The person or the system that registered this client. This is the P0 user
   * who registered the client, or the gateway that registered the client with
   * dynamic registration. */
  createdBy: { gateway: string } | { user: string; ip: string | undefined };
  /** Federation provider that validated the client */
  providerId: string;
  /** Trust authority issuer */
  issuer: string;
};

export type AgenticClient =
  | AgenticConfidentialClientBase
  | AgenticFederatedClient;

export type AgenticClientListItem = AgenticClient;

/** Response returned by GET o/:tenant/agentic/clients. */
export type AgenticClientsListResponse = {
  clients: AgenticClientListItem[];
};

/** A request to register a confidential client. The client authenticates with
 * a secret from P0. */
export type AgenticConfidentialClientCreateRequest = {
  type: "client_credential_post";
  hostname: string;
  platform: string;
  redirectUri: string;
  version: string;
};

/** A request to register an agent identity with an installed identity provider.
 *
 * Use this request if dynamic registration is disabled for the provider. Then
 * you must register each identity before it connects for the first time. P0
 * does not make a secret for these identities.
 */
export type AgenticFederatedClientCreateRequest = {
  type: "jwt_bearer";
  /** The ID of the installed `identity-provider` component that approves the
   * identity */
  providerId: string;
  /** The identity that the gateway sees. This is the verified email from the
   * agent's token if the provider supplies one. If not, this is the `sub` from
   * the token. */
  identity: string;
};

/** Request accepted by POST o/:tenant/agentic/clients. */
export type AgenticClientCreateRequest =
  | AgenticConfidentialClientCreateRequest
  | AgenticFederatedClientCreateRequest;

/** Response returned by POST o/:tenant/agentic/clients. */
export type AgenticClientCreateResponse = {
  /** The client that P0 registered. Only confidential clients have a `secret`,
   * and P0 supplies that secret only in this response. */
  client:
    | AgenticFederatedClient
    | (AgenticConfidentialClientBase & { secret?: string });
};

/** Request accepted by PATCH o/:tenant/agentic/clients/:clientId. */
export type AgenticClientUpdateRequest = {
  /** Human-friendly client name, or null to clear the saved value. */
  displayName?: string | null;
  /** Email of the P0 user who owns the client, or null to clear it. */
  owner?: string | null;
  /** Whether the client may authenticate and refresh. */
  status?: AgenticClientStatus;
};

/** Response returned by GET o/:tenant/agentic/clients/:clientId/credentials.
 * Each credential's ID is the ID of the MCP server it is bound to. */
export type AgenticClientCredentialsListResponse = {
  credentials: { id: string }[];
};

/** Response returned by PATCH o/:tenant/agentic/clients/:clientId. */
export type AgenticClientUpdateResponse = {
  client: AgenticClientListItem;
};

/** Represents an agentic client in the database */
export type AgenticConfidentialClient = AgenticConfidentialClientBase & {
  secretHash?: string;
};

/** Represents an agentic server in calls to o/:tenant/agentic/mcps/... */
export type McpServer = {
  id: string;
  logoUrl?: string;
  url: string;
} & (
  | { type: "container" }
  | { type: "internal" }
  | { type: "upstream"; upstreamUrl: string }
);

/** Response returned by GET o/:tenant/agentic/mcps. */
export type McpServersListResponse = {
  servers: McpServer[];
};

// ---------------------------------------------------------------------------
// Agent activity (GET o/:tenant/agentic/activity)
//
// Shapes come from the tenant MCP OpenAPI spec (`yarn generate:clients`).
// Fields the controller returns but the spec doesn't define are declared here
// as extensions of the generated types, and should move into the spec:
// - MCP `mcpServerId` and `resolvedRisk`, which the spec has never had
// - A2A `requestId`, which the controller returns but the spec omits
// ---------------------------------------------------------------------------

export {
  AgentActivityProtocol,
  AgentActivityRiskLevelParam,
  type AgentActivityTraceCorrelation,
} from "./hey-api-gen/tenant-mcp/index.ts";

/** Protocols an activity entry may be logged for. */
export const AgentActivityProtocols = Object.values(AgentActivityProtocol);

/** A tool call's resolved risk level, most to least severe. `Unknown` means
 * no level could be derived at all — distinct from `Low`, an affirmative
 * no-risk finding. Each level is the capitalized display form of one of the
 * spec's lowercase `AgentActivityRiskLevelParam` filter values. */
export const AgentActivityRiskLevels = [
  "Critical",
  "High",
  "Medium",
  "Low",
  "Unknown",
] as const satisfies readonly Capitalize<AgentActivityRiskLevelParam>[];
export type AgentActivityRiskLevel = (typeof AgentActivityRiskLevels)[number];

/** Values the `risk` filter accepts, most to least severe. */
export const AgentActivityRiskLevelParams = Object.values(
  AgentActivityRiskLevelParam
);

/** One IAM permission that contributed to a tool call's risk, and the
 * catalog Risk it resolved to. */
export type AgentActivityRiskPermission = {
  permission: string;
  riskId: string;
  riskName: string;
  score: AgentActivityRiskLevel;
};

/** How a tool call's `resolvedRisk` was derived: from real IAM permissions
 * resolved against the IAM Risk Catalog, when the tool maps to any;
 * otherwise from the tool's own self-declared MCP annotations. IAM, when
 * present, supersedes the annotation-derived value rather than being
 * combined with it — `resolvedRisk` always reflects exactly one source. */
export type AgentActivityRiskExplanation =
  | {
      source: "annotation";
      readOnlyHint?: boolean;
      destructiveHint?: boolean;
      idempotentHint?: boolean;
      /** True when this tool has a real IAM mapping but the catalog was
       * unavailable this read. */
      pending?: boolean;
    }
  | {
      source: "iam";
      permissions: AgentActivityRiskPermission[];
      /** Permissions this tool maps to that the catalog doesn't recognize.
       * Omitted when every mapped permission was recognized. */
      unrecognizedPermissions?: string[];
    };

/** A tool call's resolved risk level, paired with how it was derived. */
export type AgentActivityResolvedRisk = {
  level: AgentActivityRiskLevel;
  explanation: AgentActivityRiskExplanation;
};

/** Fields returned for every agent activity protocol. */
export type AgentActivityEntryCommon = TenantMcp.AgentActivityEntryCommon;

/** An MCP tool-call activity entry. */
export type McpAgentActivityEntry = TenantMcp.McpAgentActivityEntry & {
  /** The called tool's risk, when its own MCP annotations were logged and
   * resolvable, or a mapped IAM permission superseded it. */
  resolvedRisk?: AgentActivityResolvedRisk;
  /** The installed server's stable id (e.g. "gcp") — unlike `mcpServerName`,
   * a customer-configurable display label, this doesn't change if the
   * server is relabeled. */
  mcpServerId?: string;
  // Absent on MCP entries; declared so the union reads without narrowing.
  agentId?: never;
  bridgeBaseUrl?: never;
  bridgeId?: never;
  bridgePath?: never;
};

/** An A2A agent-invocation activity entry. */
export type A2aAgentActivityEntry = TenantMcp.A2aAgentActivityEntry & {
  /** Names the P0 request that gave access. */
  requestId?: string;
  // Absent on A2A entries; declared so the union reads without narrowing.
  arguments?: never;
  mcpServerId?: never;
  mcpServerName?: never;
  resolvedRisk?: never;
  toolName?: never;
};

/** A product projection of one structured gateway activity LogRecord. */
export type AgentActivityEntry = A2aAgentActivityEntry | McpAgentActivityEntry;

/** Filters accepted by tenant agentic activity queries: every query
 * parameter except pagination. */
export type AgenticActivityFilters = Omit<
  NonNullable<TenantMcp.ListAgentActivityData["query"]>,
  "pageSize" | "pageToken"
>;

export const TEXT_FILTER_PARAMS = [
  "agentId",
  "caller",
  "clientId",
  "mcpServerName",
  "sessionId",
  "toolName",
] as const satisfies readonly (keyof AgenticActivityFilters)[];
/** Filters chosen from a fixed set rather than typed, so they take a select
 * rather than an autocomplete and offer no discovered suggestions. */
export const SELECT_FILTER_PARAMS = [
  "protocol",
  "risk",
] as const satisfies readonly (keyof AgenticActivityFilters)[];
export const TIME_FILTER_PARAMS = [
  "endTime",
  "startTime",
] as const satisfies readonly (keyof AgenticActivityFilters)[];

export const AGENTIC_ACTIVITY_FILTER_KEYS = [
  ...TEXT_FILTER_PARAMS,
  ...SELECT_FILTER_PARAMS,
  ...TIME_FILTER_PARAMS,
];

export type TextFilter = (typeof TEXT_FILTER_PARAMS)[number];
export type TimeFilter = (typeof TIME_FILTER_PARAMS)[number];
export type SelectFilter = (typeof SELECT_FILTER_PARAMS)[number];

/** A gateway whose activity a read omits, and why. */
export type AgenticActivityUnavailableGateway =
  TenantMcp.AgentActivityUnavailableGateway;
export type AgenticActivityUnavailableReason =
  AgenticActivityUnavailableGateway["reason"];

/** Response returned by GET o/:tenant/agentic/activity. */
export type AgenticActivityResponse = Omit<
  TenantMcp.ListAgentActivityResponse,
  "entries"
> & {
  entries: AgentActivityEntry[];
};
